import { useState, useEffect, useRef, useCallback } from 'react'
import { debounce } from 'lodash'
import api from '../../services/api'

export default function ReadingProgressTracker({ moduleId, initialProgress = 0, initialScrollPosition = 0 }) {
  const [readingPercentage, setReadingPercentage] = useState(initialProgress)
  const [isVisible, setIsVisible] = useState(false)
  const contentRef = useRef(null)
  const lastSavedProgress = useRef(initialProgress)
  const lastSavedScroll = useRef(initialScrollPosition)

  // Calculate reading percentage based on scroll position
  const calculateReadingProgress = useCallback(() => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop
    const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight
    
    if (scrollHeight <= 0) return 0
    
    const percentage = Math.min(Math.round((scrollTop / scrollHeight) * 100), 100)
    return percentage
  }, [])

  // Save progress to backend (debounced)
  const saveProgress = useCallback(
    debounce(async (scrollPos, percentage) => {
      // Only save if there's a meaningful change (>2% or >100px scroll)
      if (
        Math.abs(percentage - lastSavedProgress.current) < 2 &&
        Math.abs(scrollPos - lastSavedScroll.current) < 100
      ) {
        return
      }

      try {
        await api.post(`/modules/${moduleId}/reading-progress`, {
          scroll_position: scrollPos,
          reading_percentage: percentage,
        })
        lastSavedProgress.current = percentage
        lastSavedScroll.current = scrollPos
      } catch (error) {
        console.error('Failed to save reading progress:', error)
      }
    }, 2000), // Debounce for 2 seconds
    [moduleId]
  )

  // Handle scroll event
  const handleScroll = useCallback(() => {
    const percentage = calculateReadingProgress()
    setReadingPercentage(percentage)
    
    // Show progress bar when user scrolls down
    setIsVisible(window.scrollY > 100)
    
    // Save progress
    const scrollPos = Math.round(window.scrollY)
    saveProgress(scrollPos, percentage)
  }, [calculateReadingProgress, saveProgress])

  // Restore scroll position on mount
  useEffect(() => {
    if (initialScrollPosition > 0) {
      // Wait for content to load, then scroll
      setTimeout(() => {
        window.scrollTo({
          top: initialScrollPosition,
          behavior: 'smooth',
        })
      }, 300)
    }
  }, [initialScrollPosition])

  // Set up scroll listener
  useEffect(() => {
    // Calculate initial percentage
    const initialPercentage = calculateReadingProgress()
    setReadingPercentage(initialPercentage)
    setIsVisible(window.scrollY > 100)

    // Add scroll listener
    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
      saveProgress.cancel() // Cancel any pending debounced calls
    }
  }, [handleScroll, calculateReadingProgress, saveProgress])

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-50 transition-transform duration-300 ${
        isVisible ? 'translate-y-0' : '-translate-y-full'
      }`}
    >
      {/* Progress Bar Container */}
      <div className="bg-white shadow-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <svg
                className="w-5 h-5 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
              <span className="text-sm font-medium text-gray-700">
                Reading Progress
              </span>
            </div>
            <span className="text-sm font-semibold text-green-600">
              {readingPercentage}%
            </span>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-green-500 to-emerald-600 transition-all duration-300 ease-out"
              style={{ width: `${readingPercentage}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
